import type { FieldSpec, PatternsFieldType } from '../../types/Field';
import type { FieldDraft, FieldPermissions } from '../../types/FieldsSource';
import type { NewFieldType } from './detectFieldType';
import type { NewFieldConfig } from './ImportState';

// Reverse of the import detector's vocabulary: maps a schema field type
// (`PatternsFieldType`, uppercase) back to the import flow's `NewFieldType`. Only
// the types the field editor can produce need entries; anything else falls back
// to `text`.
const PATTERNS_TO_NEW_FIELD_TYPE: Partial<
  Record<PatternsFieldType, NewFieldType>
> = {
  SHORT_TEXT: 'text',
  LONG_TEXT: 'text',
  NUMBER: 'number',
  DATE: 'date',
  DATETIME: 'date',
  BOOLEAN: 'boolean',
  URL: 'url',
  IMAGE: 'image',
  VIDEO: 'video',
  DOCUMENT: 'document',
  AUDIO: 'audio',
};

export const toNewFieldType = (type: PatternsFieldType): NewFieldType =>
  PATTERNS_TO_NEW_FIELD_TYPE[type] ?? 'text';

// Converts an edited field spec (the field editor's output) into the per-column
// new-field type + config the import flow stores via `setNewFieldConfig`.
export function toFieldEdit(field: FieldSpec): {
  fieldType: NewFieldType;
  displayName: string;
  config?: NewFieldConfig;
} {
  const config: NewFieldConfig = {
    ...(field.validation && { validation: field.validation }),
    ...(field.isPii && { isPii: true }),
    ...(field.helpText && { helpText: field.helpText }),
  };
  return {
    fieldType: toNewFieldType(field.type),
    displayName: field.displayName,
    config: Object.keys(config).length > 0 ? config : undefined,
  };
}

// Coarse `NewFieldType` for a DataExtension `CustomFieldType` id — only used for
// the config-step helper text ("This will be a new {type} field."). The precise
// DataExtension type the user picked is preserved separately on `config.deType`.
const DATA_EXTENSION_TO_NEW_FIELD_TYPE: Record<string, NewFieldType> = {
  shortText: 'text',
  longText: 'text',
  url: 'url',
  date: 'date',
  dateTime: 'date',
  integer: 'number',
  decimal: 'number',
  checkbox: 'boolean',
  multiSelect: 'text',
  files: 'document',
};

// Converts the DataExtension field editor's output (a source-neutral `FieldDraft`
// carrying per-audience read/write permissions) into the per-column new-field
// type + config the import flow stores via `setNewFieldConfig`. The precise
// DataExtension type is kept on `config.deType`; `_createPendingFields` reads it
// when building the create-field call.
export function toDataExtensionFieldEdit(draft: FieldDraft): {
  fieldType: NewFieldType;
  displayName: string;
  config?: NewFieldConfig;
} {
  const permissions: FieldPermissions = {
    installedApps: {
      defaultRead: draft.readApps,
      defaultWrite: draft.writeApps,
    },
    siteVisitors: {
      defaultRead: draft.readUsers,
      defaultWrite: draft.writeUsers,
    },
  };
  const config: NewFieldConfig = {
    permissions,
    ...(draft.type && { deType: draft.type }),
    ...(draft.containsPii && { isPii: true }),
  };
  return {
    fieldType: draft.type
      ? DATA_EXTENSION_TO_NEW_FIELD_TYPE[draft.type] ?? 'text'
      : 'text',
    displayName: draft.name,
    config,
  };
}
